import { Injectable, inject } from '@angular/core';
import { NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { ComponentStore } from '@ngrx/component-store';
import { DateConversionService } from './date-conversion.service';
import { Observable, map, take, withLatestFrom } from 'rxjs';
import {DatePickerAdvancedChanges, DefaultRange} from '../datepicker-range-advanced.interfaces';

export interface CalendarDatesSelected {
  startDate: NgbDate | null;
  endDate: NgbDate | null;
}

@Injectable({providedIn: 'root'})
export class DateSelectionService extends ComponentStore<CalendarDatesSelected> {
  dateConversionService = inject(DateConversionService);
  constructor() {
    super({
      startDate: null,
      endDate: null,
    });
  }

  readonly selectedDates$ = this.select((state) => state);
  readonly selectedStartDate$: Observable<Date> = this.selectedDates$.pipe(
    map((dates) => (dates.startDate ? this.dateConversionService.toDate(dates.startDate) : null))
  );
  readonly selectedEndDate$: Observable<Date> = this.selectedDates$.pipe(
    map((dates) => (dates.endDate ? this.dateConversionService.toDate(dates.endDate) : null))
  );

  handleDateSelection = this.updater((state, date: NgbDate) => {
    const { startDate, endDate } = state;
    if (!startDate) {
      return {
        ...state,
        startDate: date,
      };
    }

    if (!endDate) {
      if (date.before(startDate)) {
        return {
          ...state,
          startDate: date,
          endDate: startDate,
        };
      }
      return {
        ...state,
        endDate: date,
      };
    }

    return {
      ...state,
      startDate: date,
      endDate: null,
    };
  });

  setDates(dates: { startDate: Date | null; endDate: Date | null }): void {
    const startDate = dates.startDate ? this.dateConversionService.toNgbDate(dates.startDate) : null;
    const endDate = dates.endDate ? this.dateConversionService.toNgbDate(dates.endDate) : null;
    return this.setState((state) => ({
      ...state,
      startDate,
      endDate,
    }));
  }

  getDatesChangeEvent(dateType: string | null = null): DatePickerAdvancedChanges {
    const { startDate, endDate } = this.getSelectedDates();
    return {
      dateType,
      startDate: this.dateConversionService.ngbDateToString(startDate),
      endDate: endDate ? this.dateConversionService.ngbDateToString(endDate) : this.dateConversionService.ngbDateToString(startDate),
    };
  }

  private getSelectedDates(): CalendarDatesSelected {
    let state: CalendarDatesSelected;
    this.selectedDates$.pipe(take(1)).subscribe(selectedDates => (state = selectedDates));
    return state;
  }

  
  isInside$(date: NgbDate): Observable<boolean> {
    return this.selectedDates$.pipe(
      map((datesSelected) => date.after(datesSelected.startDate) && date.before(datesSelected.endDate))
    );
  }

  isRange$(date: NgbDate): Observable<boolean> {
    return this.selectedDates$.pipe(
      withLatestFrom(this.isInside$(date), this.isHovered$(date)),
      map(
        ([datesSelected, isInside, isHovered]) =>
          date.equals(datesSelected.startDate) || date.equals(datesSelected.endDate) || isInside || isHovered
      )
    );
  }

  isHovered$(date: NgbDate, hoveredDate: NgbDate | null = null): Observable<boolean> {
    return this.selectedDates$.pipe(
      map(
        ({startDate, endDate}) =>
          startDate &&
          !endDate &&
          hoveredDate &&
          date.after(startDate) &&
          date.before(hoveredDate)
      )
    );
  }

  selectAllDaysInMonth(datePicker: NgbDatepicker, month: number): void {
    const { state, calendar } = datePicker;
    const startDate = new NgbDate(state.firstDate.year, month, 1);
    const endDate = calendar.getPrev(calendar.getNext(startDate, 'm'), 'd');
    return this.setState((state) => ({
      ...state,
      startDate,
      endDate,
    }));
  }

  getEndDate(endDate: Date | string, minDate: Date | string,  maxDate: Date | string): Date | string {
    if (!endDate) {
      return DefaultRange.endDate;
    }
    if (new Date(endDate) > new Date(maxDate)) {
      return maxDate;
    }
    if (new Date(endDate) < new Date(minDate)) {
      return minDate;
    }
    return  endDate;
  }

  getStartDate(startDate: Date | string, minDate: Date | string, maxDate: Date | string): Date | string {
    if (!startDate) {
      return DefaultRange.startDate;
    }
    if (new Date(startDate) < new Date(minDate)) {
      return minDate;
    }
    if (new Date(startDate) > new Date(maxDate)) {
      return maxDate;
    }
    return  startDate;
  }
}
